import React, { useState, useRef, useEffect } from 'react';
import PropTypes from 'prop-types';
import classnames from 'classnames';

/**
 * Combobox
 * A custom searchable select component since Bootstrap doesn't offer one.
 * Uses an input as the main trigger for inline searching.
 */
export default function Combobox({
  value,
  options,
  placeholder = 'Select...',
  emptyText = 'No results found',
  onChange,
  renderOption,
  title = undefined,
  className,
  disabled = false,
}) {
  const [isOpen, setIsOpen] = useState(false);
  const [search, setSearch] = useState('');
  const [activeIndex, setActiveIndex] = useState(-1);

  const containerRef = useRef(null);
  const inputRef = useRef(null);
  const listboxRef = useRef(null);
  const isKeyboardNav = useRef(false);

  const selectedOption = options.find((o) => o.value === value);

  // If the search text exactly matches the selected option's label,
  // it means the user just opened the dropdown and hasn't typed a new query yet.
  // In this case, we show all options instead of filtering to just the selected one.
  const isSearchMatchingSelected = selectedOption && search === selectedOption.label;
  const filteredOptions = isSearchMatchingSelected
    ? options
    : options.filter((option) => option.label.toLowerCase().includes(search.toLowerCase()));

  // Sync search input with selected option when closed
  useEffect(() => {
    if (!isOpen) {
      setSearch(selectedOption ? selectedOption.label : '');
    }
  }, [isOpen, selectedOption]);

  // Focus management
  useEffect(() => {
    if (isOpen && inputRef.current) {
      // Set active index to selected item or 0
      const selectedIndex = options.findIndex((o) => o.value === value);
      setActiveIndex(selectedIndex >= 0 ? selectedIndex : 0);
    }
  }, [isOpen, value, options]);

  // Scroll active item into view
  useEffect(() => {
    if (isOpen && activeIndex >= 0 && listboxRef.current && isKeyboardNav.current) {
      const activeEl = listboxRef.current.children[activeIndex];
      if (activeEl && activeEl.scrollIntoView) {
        activeEl.scrollIntoView({ block: 'nearest' });
      }
    }
  }, [activeIndex, isOpen]);

  // Click outside to close
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (containerRef.current && !containerRef.current.contains(e.target)) {
        setIsOpen(false);
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen]);

  const handleToggle = () => {
    if (disabled) return;
    setIsOpen((prev) => !prev);
    if (!isOpen && inputRef.current) {
      inputRef.current.focus();
    }
  };

  const handleSelect = (optionValue) => {
    onChange(optionValue);
    setIsOpen(false);
    // Search is updated by useEffect when isOpen becomes false
  };

  const handleKeyDown = (e) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'Enter') {
        e.preventDefault();
        isKeyboardNav.current = true;
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown':
        e.preventDefault();
        isKeyboardNav.current = true;
        setActiveIndex((prev) => (prev < filteredOptions.length - 1 ? prev + 1 : prev));
        break;
      case 'ArrowUp':
        e.preventDefault();
        isKeyboardNav.current = true;
        setActiveIndex((prev) => (prev > 0 ? prev - 1 : prev));
        break;
      case 'Enter':
        e.preventDefault();
        e.stopPropagation(); // prevent submitting forms
        if (activeIndex >= 0 && activeIndex < filteredOptions.length) {
          handleSelect(filteredOptions[activeIndex].value);
        }
        break;
      case 'Escape':
        e.preventDefault();
        e.stopPropagation();
        setIsOpen(false);
        break;
      case 'Tab':
        setIsOpen(false);
        break;
      default:
        break;
    }
  };

  return (
    <div ref={containerRef} className={classnames('position-relative', className)}>
      <div
        className={classnames('form-control d-flex align-items-center p-0 position-relative shadow-none', {
          'bg-light': disabled,
        })}
        style={{ cursor: disabled ? 'not-allowed' : 'text' }}
        role="button"
        tabIndex={0}
        onKeyDown={(e) => {
          if (e.target !== e.currentTarget) {
            return; // keys typed in the input are the input's
          }
          if (e.key === 'Enter' || e.key === ' ') {
            e.preventDefault();
            if (!disabled) {
              inputRef.current?.focus();
              setIsOpen(true);
            }
          }
        }}
        onClick={() => {
          if (!disabled) {
            inputRef.current?.focus();
            setIsOpen(true);
          }
        }}
      >
        <input
          ref={inputRef}
          type="text"
          className="form-control border-0 shadow-none bg-transparent py-2"
          style={{ paddingRight: '30px' }}
          placeholder={placeholder}
          title={title}
          value={search}
          onChange={(e) => {
            setSearch(e.target.value);
            if (!isOpen) setIsOpen(true);
            isKeyboardNav.current = false;
            setActiveIndex(0);
          }}
          onFocus={() => {
            setIsOpen(true);
            inputRef.current?.select();
          }}
          onKeyDown={handleKeyDown}
          role="combobox"
          aria-expanded={isOpen}
          aria-autocomplete="list"
          aria-controls="combobox-listbox"
          disabled={disabled}
        />
        <button
          type="button"
          className="btn position-absolute end-0 top-0 bottom-0 d-flex align-items-center px-2 shadow-none border-0"
          style={{ cursor: disabled ? 'not-allowed' : 'pointer' }}
          onClick={(e) => {
            e.stopPropagation();
            handleToggle();
          }}
          aria-label="Toggle options"
          disabled={disabled}
        >
          <i className="fa-solid fa-chevron-down text-muted" style={{ fontSize: '0.8em' }} />
        </button>
      </div>

      {isOpen && (
        <div className="mt-1 bg-white border rounded shadow-sm w-100">
          {/* Listbox */}
          <ul
            id="combobox-listbox"
            ref={listboxRef}
            className="list-unstyled mb-0 overflow-auto py-1"
            style={{ maxHeight: '225px' }}
            role="listbox"
          >
            {filteredOptions.length === 0 ? (
              <li className="text-muted text-center py-2 small">{emptyText}</li>
            ) : (
              filteredOptions.map((option, index) => {
                const isActive = index === activeIndex;
                const isSelected = option.value === value;
                return (
                  <li
                    key={option.value}
                    role="option"
                    aria-selected={isSelected}
                    onMouseEnter={() => {
                      isKeyboardNav.current = false;
                      setActiveIndex(index);
                    }}
                  >
                    <button
                      type="button"
                      className={classnames(
                        'btn w-100 text-start px-3 py-3 small d-flex align-items-center justify-content-between border-0 rounded-0 shadow-none',
                        {
                          'bg-light': isActive && !isSelected,
                          'bg-primary-10 fw-bold text-primary': isSelected,
                          'text-body': !isSelected,
                        },
                      )}
                      onClick={() => handleSelect(option.value)}
                      // On the row, not the label: hovering the padding or icon should show
                      // the full text too, and long labels are clipped by the row's width.
                      title={typeof option.label === "string" ? option.label : undefined}
                    >
                      {/* minWidth:0 lets a text-truncate child actually clip — without it a
                          flex item refuses to shrink below its content width. */}
                      <span className="d-flex align-items-center gap-2 overflow-hidden" style={{ minWidth: 0 }}>
                        {renderOption ? renderOption(option) : option.label}
                      </span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </div>
      )}
    </div>
  );
}

Combobox.propTypes = {
  value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]),
  options: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.oneOfType([PropTypes.string, PropTypes.number]).isRequired,
      label: PropTypes.string.isRequired,
    }),
  ).isRequired,
  placeholder: PropTypes.string,
  emptyText: PropTypes.string,
  onChange: PropTypes.func.isRequired,
  renderOption: PropTypes.func,
  title: PropTypes.string,
  className: PropTypes.string,
  disabled: PropTypes.bool,
};
